import cardValidator from 'card-validator'
import {ChangeEvent, FC, useState, KeyboardEvent, useRef} from 'react'
import {useDispatch} from 'react-redux'
import {
  CardBrands,
  CorefyCardContextType,
  PaymentMethodType,
  PaymentOrder,
  PaymentProviderType,
  PaymentStatus,
  setPackageModalOpen,
  setPaymentModal,
  useConfirmPayModal
} from 'entities/payment'
import {icons} from 'features/cardItem'
import {NUMBER_REGEX} from 'shared/lib'
import {redirectToOtherSite} from 'shared/lib/links'
import {Form} from 'shared/ui/form'
import {DefaultCreditCard} from 'shared/ui/icons'
import {TextField} from 'shared/ui/textField'
import {ConfirmButton} from '../confirmButton'
import {ReactComponent as CVVTooltip1} from './img/cvv-tooltip-1.svg'
import {ReactComponent as CVVTooltip2} from './img/cvv-tooltip-2.svg'
import {ReactComponent as QuestionIcon} from './img/question-icon.svg'
import styles from './styles.module.scss'

type PayFormProps = {
  onCloseHandler: () => void
  onErrorHandler: (e: unknown) => void
}

export const PayForm: FC<PayFormProps> = ({onCloseHandler, onErrorHandler}) => {
  const dispatch = useDispatch()
  const cvvRef = useRef<HTMLInputElement | null>(null)
  const [isShowTooltip, setIsShowTooltip] = useState(false)
  const [cardBrand, setCardBrand] = useState<CardBrands | undefined>(undefined)
  const {isOpen, ...openPackage} = useConfirmPayModal()
  const [fields, setFields] = useState({
    cardNumber: '',
    expireDate: '',
    cvv: '',
    cardHolder: ''
  })
  const [validFields, setValidFields] = useState({
    cardNumber: true,
    expireDate: true,
    cvv: true,
    cardHolder: true
  })

  const onChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const {name, value} = e.target
    let formattedValue = value
    // Check format card number
    if (name === 'cardNumber') {
      //only number
      if (!/^\d*$/.test(formattedValue.replace(/-|\s/g, ''))) return

      formattedValue = value
        .replace(/\s/g, '')
        .match(/.{1,4}/g)
        ?.join(' ') || value

      const {isPotentiallyValid, card} = cardValidator.number(formattedValue)
      setValidFields((prevState) => ({...prevState, [name]: isPotentiallyValid}))
      setCardBrand(card?.type as CardBrands)
    }

    if (name === 'expireDate') {
      // Удаляем все нечисловые символы
      const inputValue = value.replace(/[^0-9]/g, '')

      if (inputValue.length > 2) {
        const month = inputValue.slice(0, 2)
        const year = inputValue.slice(2, 4)
        formattedValue = `${month}/${year}`
      } else {
        formattedValue = inputValue
      }

      const {isPotentiallyValid} = cardValidator.expirationDate(formattedValue)
      setValidFields((prevState) => ({...prevState, [name]: isPotentiallyValid}))
    }

    if (name === 'cvv') {
      if (!NUMBER_REGEX.test(value) && value !== '') return

      setValidFields((prevState) => ({...prevState, [name]: true}))
    }

    if (name === 'cardHolder') {
      const {isPotentiallyValid} = cardValidator.cardholderName(formattedValue)

      setValidFields((prevState) => ({...prevState, [name]: isPotentiallyValid}))
    }

    setFields((prevState) => ({...prevState, [name]: formattedValue}))
  }

  const onHoverHandler = () => setIsShowTooltip(true)

  const onLeaveHandler = () => setIsShowTooltip(false)

  const onClickHandler = async () => {
    const {isValid: cardNumber, card} = cardValidator.number(fields.cardNumber)
    const {isValid: expireDate} = cardValidator.expirationDate(fields.expireDate)
    const {isValid: cvv} = cardValidator.cvv(fields.cvv, card?.code.size)
    const {isValid: cardHolder} = cardValidator.cardholderName(fields.cardHolder)
    if (!cardNumber || !expireDate || !cvv) {
      setValidFields((prevState) => ({...prevState, cardNumber, expireDate, cvv, cardHolder}))
      //sentry error
      throw new Error(`not valid card data, cardNumber: ${!cardNumber ? fields.cardNumber : false}, cardHolder: ${!cardHolder} cvv: ${!cvv}`)
    }


    dispatch(setPaymentModal({isOpen: true, status: PaymentStatus.IN_PROGRESS}))
    onCloseHandler()
    dispatch(setPackageModalOpen(false))

    try {
      const {data} = await PaymentOrder({
        provider: PaymentProviderType.COREFY,
        method: PaymentMethodType.CARD,
        packageId: openPackage.id,
        cvv: fields.cvv,
        cardHolder: fields.cardHolder,
        cardNumber: fields.cardNumber.replace(/ /g, ''),
        expireMonth: Number(fields.expireDate.slice(0, 2)),
        expireYear: Number(fields.expireDate.slice(3))
      })

      if (data.status === 'error') throw new Error('Internal server error')

      const context = data.context as CorefyCardContextType
      if (context.success && context.threeDs) {
        redirectToOtherSite(
          `${window.location.origin}/threeDs/${btoa(
            JSON.stringify(context.threeDs)
          )}`
        )
      }
    } catch (e) {
      onErrorHandler(e)
    }
  }

  const onKeyDownHandler = (e: KeyboardEvent<HTMLInputElement>) => {
    const isNumberKey = /^[0-9]$/.test(e.key) // Проверяем, является ли нажатая клавиша числом
    const isDeleteKey = e.key === 'Delete' || e.key === 'Backspace' // Проверяем, является ли нажатая клавиша "Delete" или "Backspace"

    if (!isNumberKey && !isDeleteKey) {
      e.preventDefault()
    }
    if (e.key === 'Tab') {
      cvvRef.current?.focus()
      return
    }
    // @ts-ignore
    const cursorPosition = e.target.selectionStart
    // @ts-ignore
    if (isDeleteKey && e.target.value.includes('/') && cursorPosition > 0) {
      e.preventDefault() // Предотвращаем удаление символа "/", если он присутствует в инпуте
      // @ts-ignore
      e.target.value = e.target.value.slice(0, cursorPosition - 1) + e.target.value.slice(cursorPosition)
    }
  }

  return (
    <Form className={styles.payFrom} onSubmit={onClickHandler}>
      <div className={styles.card}>
        <label className={styles.cardLabel} htmlFor='cardNumber'>
          Card
        </label>
        <div className={styles.cardFields}>
          <div className={`
          ${styles.inputContainer} 
          ${styles.inputContainerNumber} 
          ${!validFields.cardNumber ? styles.inputContainer_error : ''}
          `}>
            <div>
              {cardBrand ? icons[cardBrand] : <DefaultCreditCard />}
            </div>
            <input
              autoFocus
              required
              name='cardNumber'
              value={fields.cardNumber}
              onChange={onChangeHandler}
              placeholder='0000 0000 0000 0000'
              minLength={12}
              maxLength={19}
            />
          </div>
          <div className={`
          ${styles.inputContainer} 
          ${styles.inputContainerDate}
          ${!validFields.expireDate ? styles.inputContainer_error : ''}
          `}
          >
            <input
              required
              className={styles.cardNumber}
              onKeyDown={(e) => onKeyDownHandler(e)}
              name='expireDate'
              value={fields.expireDate}
              onChange={onChangeHandler}
              placeholder='MM/YY'
              minLength={5}
              maxLength={5}
            />
          </div>
          <div
            className={`
            ${styles.inputContainer} 
            ${styles.inputContainerCode}
            ${!validFields.cvv ? styles.inputContainer_error : ''}
            `}
          >
            <input
              onChange={onChangeHandler}
              name='cvv'
              value={fields.cvv}
              placeholder='CVV'
              type='password'
              size={1}
              minLength={3}
              maxLength={4}
              ref={cvvRef}
            />
            <div
              className={styles.cardTooltipWrapper}
              onMouseEnter={onHoverHandler}
              onMouseLeave={onLeaveHandler}
              onMouseOut={onLeaveHandler}
              onMouseOver={onHoverHandler}
            >
              <QuestionIcon />
              {isShowTooltip ? (
                <div className={styles.cardTooltip}>
                  <CVVTooltip1 />
                  <CVVTooltip2 />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      <TextField
        required
        error={!validFields.cardHolder ? ' ' : ''}
        isRequiredLabel={false}
        onChange={onChangeHandler}
        name='cardHolder'
        value={fields.cardHolder}
        label='Cardholder’s name'
        placeholder='Your name'
        type='text'
      />
      <ConfirmButton type='submit' />
    </Form>
  )
}
